import { useNavigate } from "@tanstack/react-router";
import { useId, useMemo, useState, type FormEvent } from "react";
import { z } from "zod";
import type { AgentSettings, Channel, MeView, Persona } from "../../shared/contracts";
import { PERSONA_VALUES } from "../../shared/contracts";
import { isValidHm, isValidTimezone } from "../../shared/time";
import { MAIN_DEFAULT_LOOK } from "../../shared/character";
import { Perso } from "../components/avatar";
import { Button, Eyebrow, Field, Input, Notice, RadioCard, Select, Textarea } from "../components/ui";
import { api, errorText } from "../lib/api";
import { PERSONAS, PRIMARY_CHANNEL_OPTIONS } from "../lib/format";
import { usePageTitle } from "../lib/hooks";
import { setMe } from "../lib/session";
import { mutateResource } from "../lib/store";

export function browserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimezone(tz) ? tz : "Europe/Paris";
  } catch {
    return "Europe/Paris";
  }
}

export function timezoneList(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: "timeZone") => string[] };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf("timeZone") : [];
  } catch {
    return [];
  }
}

const schema = z.object({
  userName: z.string().trim().min(1, "Dis-moi ton prénom.").max(60, "60 caractères maximum."),
  agentName: z.string().trim().min(1, "Donne un nom à ton agent.").max(40, "40 caractères maximum."),
  persona: z.enum(PERSONA_VALUES),
  personaNotes: z.string().trim().max(500, "500 caractères maximum."),
  timezone: z.string().trim().refine(isValidTimezone, "Fuseau horaire inconnu (ex. Europe/Paris)."),
  briefTime: z.string().trim().refine(isValidHm, "Heure invalide (format HH:MM)."),
  primaryChannel: z.enum(["web", "imessage", "telegram", "email"]),
});

type Errors = Partial<Record<keyof z.infer<typeof schema>, string>>;

/** /bienvenue — first-run form, then the agent wakes up and writes first in /discussion. */
export function OnboardingPage({ me }: { me: MeView }) {
  usePageTitle("Bienvenue");
  const navigate = useNavigate();
  const [userName, setUserName] = useState(me.name ?? "");
  const [agentName, setAgentName] = useState("Nova");
  const [focused, setFocused] = useState(false);
  const [persona, setPersona] = useState<Persona>("chaleureux");
  const [personaNotes, setPersonaNotes] = useState("");
  const [timezone, setTimezone] = useState(browserTimezone);
  const [briefTime, setBriefTime] = useState("07:30");
  // A phone account (created by text or with its number) already has its verified iMessage link.
  const [primaryChannel, setPrimaryChannel] = useState<Channel>(me.phone ? "imessage" : "web");
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const zones = useMemo(timezoneList, []);
  const ids = {
    userName: useId(),
    agentName: useId(),
    notes: useId(),
    timezone: useId(),
    zones: useId(),
    briefTime: useId(),
    channel: useId(),
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const parsed = schema.safeParse({ userName, agentName, persona, personaNotes, timezone, briefTime, primaryChannel });
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof Errors | undefined;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      setError("Il manque quelque chose : regarde les champs indiqués.");
      return;
    }
    setErrors({});
    setError(null);
    setBusy(true);
    try {
      const v = parsed.data;
      const res = await api("POST /api/onboarding", {
        body: {
          userName: v.userName,
          agentName: v.agentName,
          persona: v.persona,
          ...(v.personaNotes ? { personaNotes: v.personaNotes } : {}),
          timezone: v.timezone,
          briefTime: v.briefTime,
          primaryChannel: v.primaryChannel,
        },
      });
      mutateResource<AgentSettings>("settings", () => res.settings);
      setMe(res.me);
      await navigate({ to: "/discussion", replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh px-4 pt-10 pb-16">
      <form
        onSubmit={onSubmit}
        noValidate
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="mx-auto flex w-full max-w-[560px] flex-col gap-[26px]"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <Perso look={MAIN_DEFAULT_LOOK} mood={busy ? "joie" : focused ? "ecoute" : "repos"} size={120} label={`${agentName.trim() || "Nova"}, ton agent`} />
          <h1 className="font-display text-[30px] leading-tight font-medium">Faisons connaissance</h1>
        </div>

        <section aria-labelledby={`${ids.userName}-s`} className="flex flex-col gap-3">
          <Eyebrow>
            <span id={`${ids.userName}-s`}>Toi</span>
          </Eyebrow>
          <div className="flex flex-col gap-4 rounded-[20px] bg-elev p-4 sm:p-5">
            <Field label="Ton prénom" htmlFor={ids.userName} error={errors.userName}>
              <Input id={ids.userName} autoComplete="given-name" value={userName} maxLength={60} onChange={(e) => setUserName(e.target.value)} className="bg-bg" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Fuseau horaire" htmlFor={ids.timezone} error={errors.timezone}>
                <Input
                  id={ids.timezone}
                  list={zones.length ? ids.zones : undefined}
                  value={timezone}
                  autoCapitalize="none"
                  spellCheck={false}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="bg-bg"
                />
                {zones.length ? (
                  <datalist id={ids.zones}>
                    {zones.map((z) => (
                      <option key={z} value={z} />
                    ))}
                  </datalist>
                ) : null}
              </Field>
              <Field label="Heure du brief" htmlFor={ids.briefTime} error={errors.briefTime}>
                <Input id={ids.briefTime} type="time" value={briefTime} onChange={(e) => setBriefTime(e.target.value)} className="bg-bg" />
              </Field>
            </div>
            <Field label="Canal préféré" htmlFor={ids.channel} hint={me.phone ? "Ton numéro est déjà relié." : "Ton téléphone se relie juste après."}>
              <Select id={ids.channel} value={primaryChannel} onChange={(e) => setPrimaryChannel(e.target.value as Channel)} className="bg-bg">
                {PRIMARY_CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </section>

        <section aria-labelledby={`${ids.agentName}-s`} className="flex flex-col gap-3">
          <Eyebrow>
            <span id={`${ids.agentName}-s`}>Ton agent</span>
          </Eyebrow>
          <div className="flex flex-col gap-4 rounded-[20px] bg-elev p-4 sm:p-5">
            <Field label="Nom de ton agent" htmlFor={ids.agentName} error={errors.agentName}>
              <Input id={ids.agentName} value={agentName} maxLength={40} onChange={(e) => setAgentName(e.target.value)} className="bg-bg" />
            </Field>
            <fieldset>
              <legend className="mb-2 text-[13px] font-medium">Sa personnalité</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {PERSONAS.map((p) => (
                  <RadioCard key={p.value} name="persona" value={p.value} label={p.label} checked={persona === p.value} onChange={(v) => setPersona(v as Persona)}>
                    {p.description}
                  </RadioCard>
                ))}
              </div>
            </fieldset>
            <Field label="Consignes de style (facultatif)" htmlFor={ids.notes} error={errors.personaNotes} hint="Ex. « Sois bref le matin. »">
              <Textarea id={ids.notes} value={personaNotes} maxLength={500} className="min-h-20 bg-bg" onChange={(e) => setPersonaNotes(e.target.value)} />
            </Field>
          </div>
        </section>

        {error ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}

        <div className="flex flex-col items-center gap-3">
          <Button type="submit" size="lg" className="h-[52px] w-full text-[16px]" busy={busy}>
            Réveiller mon agent
          </Button>
          <p className="text-center text-[12.5px] text-subtle">Tout se modifie ensuite.</p>
        </div>
      </form>
    </div>
  );
}
